import { toMajor, type Currency, type Invoice, type InvoiceLanguage, type InvoiceLine } from "@otrack/domain";

/**
 * The words and number formats printed on an Invoice, in the Client's Invoice Language. The rest
 * of ontrack stays in English; only the document a client receives is translated.
 */
export interface InvoiceCopy {
  locale: string;
  invoice: string;
  draft: string;
  from: string;
  billedTo: string;
  issued: string;
  due: string;
  period: string;
  reference: string;
  description: string;
  quantity: string;
  rate: string;
  amount: string;
  subtotal: string;
  discount: string;
  amountDue: string;
  paidOn: (date: string) => string;
  general: string;
  deposit: (percent: string) => string;
  balance: string;
  /** "Installment 1 of 2" under the number of a split Invoice. */
  installment: (index: number, count: number) => string;
  payment: string;
  note: string;
  taxId: string;
  onSend: string;
  dueAfter: (days: number) => string;
  hours: string;
}

const COPY: Record<InvoiceLanguage, InvoiceCopy> = {
  en: {
    locale: "en-US",
    invoice: "Invoice",
    draft: "Draft",
    from: "From",
    billedTo: "Billed to",
    issued: "Issued",
    due: "Due",
    period: "Period",
    reference: "Reference",
    description: "Description",
    quantity: "Qty",
    rate: "Rate",
    amount: "Amount",
    subtotal: "Subtotal",
    discount: "Discount",
    amountDue: "Amount due",
    paidOn: (date) => `Paid on ${date}. Thank you.`,
    general: "General",
    deposit: (percent) => `Deposit ${percent}`,
    balance: "Balance after deposit",
    installment: (index, count) => `Installment ${index} of ${count}`,
    payment: "How to pay",
    note: "Note",
    taxId: "Tax ID",
    onSend: "When sent",
    dueAfter: (days) => (days ? `${days} days after issue` : "On receipt"),
    hours: "h"
  },
  es: {
    locale: "es-ES",
    invoice: "Factura",
    draft: "Borrador",
    from: "De",
    billedTo: "Facturar a",
    issued: "Emisión",
    due: "Vence",
    period: "Período",
    reference: "Referencia",
    description: "Descripción",
    quantity: "Cant.",
    rate: "Precio",
    amount: "Importe",
    subtotal: "Subtotal",
    discount: "Descuento",
    amountDue: "Total a pagar",
    paidOn: (date) => `Pagada el ${date}. Gracias.`,
    general: "General",
    deposit: (percent) => `Anticipo ${percent}`,
    balance: "Saldo tras el anticipo",
    installment: (index, count) => `Cuota ${index} de ${count}`,
    payment: "Forma de pago",
    note: "Nota",
    taxId: "Identificación fiscal",
    onSend: "Al enviar",
    dueAfter: (days) => (days ? `${days} días tras la emisión` : "Al recibirla"),
    hours: "h"
  }
};

export function invoiceCopy(language: InvoiceLanguage): InvoiceCopy {
  return COPY[language];
}

/** Money in the document's locale. Spanish groups thousands even in four-digit amounts. */
export function docMoney(amount: number, currency: Currency, copy: InvoiceCopy): string {
  const symbolic = currency === "USD" || currency === "EUR";
  return new Intl.NumberFormat(copy.locale, {
    style: "currency",
    currency,
    currencyDisplay: symbolic ? "narrowSymbol" : "code",
    useGrouping: "always"
  }).format(toMajor(amount, currency));
}

export function docDate(ts: number, copy: InvoiceCopy): string {
  return new Intl.DateTimeFormat(copy.locale, { day: "numeric", month: "short", year: "numeric" }).format(ts);
}

/** "Oct 1 - 31, 2026" in English, "1 - 31 oct 2026" in Spanish, a single day when they match. */
export function docPeriod(invoice: Pick<Invoice, "periodFrom" | "periodTo">, copy: InvoiceCopy): string {
  const format = new Intl.DateTimeFormat(copy.locale, { day: "numeric", month: "short", year: "numeric" });
  if (invoice.periodFrom === invoice.periodTo) return format.format(invoice.periodFrom);
  return format.formatRange(invoice.periodFrom, invoice.periodTo).replace(/\s*[–—]\s*/g, " - ");
}

export function docPercent(basisPoints: number, copy: InvoiceCopy): string {
  return new Intl.NumberFormat(copy.locale, { style: "percent", maximumFractionDigits: 2 }).format(basisPoints / 10_000);
}

export function docQuantity(line: InvoiceLine, copy: InvoiceCopy): string {
  if (line.kind === "hours") return `${new Intl.NumberFormat(copy.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(line.quantity)} ${copy.hours}`;
  return new Intl.NumberFormat(copy.locale, { maximumFractionDigits: 3 }).format(line.quantity);
}

/** A line's main text: time without a Task reads "General"; Deposits say how much of the price. */
export function lineTitle(line: InvoiceLine, copy: InvoiceCopy): string {
  if (line.kind === "deposit") return `${copy.deposit(docPercent(line.percent ?? 0, copy))}, ${line.title ?? ""}`;
  return line.title ?? copy.general;
}
